// MaturityNumber Component
// Число зрелости — проявляется после 40-50 лет, синтез Life Path + Destiny

import React from 'react';

interface MaturityNumberData {
  number: number;
  lifePathNumber: number;
  destinyNumber: number;
  description: string;
  interpretation: string;
  aiInterpretation?: string;
  recommendations: string[];
}

interface MaturityNumberProps {
  data: MaturityNumberData;
  loading?: boolean;
}

const MATURITY_COLOR = '#F57C00';

const cardStyle: React.CSSProperties = {
  background: 'rgba(13, 25, 48, 0.6)',
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(245, 124, 0, 0.3)',
  borderRadius: '20px',
  padding: '24px',
  marginBottom: '20px',
};

const numberStyle: React.CSSProperties = {
  fontSize: 'clamp(48px, 8vw, 72px)',
  fontFamily: 'var(--font-display)',
  fontStyle: 'italic',
  fontWeight: 700,
  textAlign: 'center',
  margin: '16px 0',
  textShadow: '0 0 30px rgba(245, 124, 0, 0.5)',
};

const sectionTitleStyle: React.CSSProperties = {
  color: '#F57C00',
  fontSize: '16px',
  fontFamily: 'var(--font-display)',
  fontStyle: 'italic',
  fontWeight: 600,
  marginBottom: '12px',
  marginTop: '20px',
};

const textStyle: React.CSSProperties = {
  color: '#e0e0e0',
  fontSize: '14px',
  lineHeight: 1.7,
  fontFamily: 'var(--font-sans)',
  marginBottom: '8px',
};

const listItemStyle: React.CSSProperties = {
  color: '#d0d0d0',
  fontSize: '14px',
  lineHeight: 1.6,
  fontFamily: 'var(--font-sans)',
  paddingLeft: '20px',
  marginBottom: '6px',
  position: 'relative' as const,
};

const aiTextStyle: React.CSSProperties = {
  color: '#e0e0e0',
  fontSize: '14px',
  lineHeight: 1.7,
  fontFamily: 'var(--font-sans)',
  whiteSpace: 'pre-wrap',
  padding: '16px',
  background: 'rgba(245, 124, 0, 0.1)',
  borderRadius: '12px',
  border: '1px solid rgba(245, 124, 0, 0.2)',
};

const bulletStyle: React.CSSProperties = {
  position: 'absolute' as const,
  left: '4px',
  color: '#F57C00',
};

const smallNumberCardStyle: React.CSSProperties = {
  background: 'rgba(10, 8, 20, 0.4)',
  border: '1px solid rgba(245, 124, 0, 0.2)',
  borderRadius: '12px',
  padding: '16px',
  textAlign: 'center',
};

const MaturityNumber: React.FC<MaturityNumberProps> = ({ data, loading = false }) => {
  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px 0' }}>
        <p style={{ color: '#888' }}>Расчёт числа зрелости...</p>
      </div>
    );
  }

  return (
    <div>
      {/* Главное число */}
      <div style={{ ...cardStyle, textAlign: 'center' }}>
        <h3 style={{
          color: MATURITY_COLOR,
          fontSize: '20px',
          fontFamily: 'var(--font-display)',
          fontStyle: 'italic',
          marginBottom: '8px',
        }}>
          ⭐ Число зрелости
        </h3>

        <div style={{ fontSize: '48px', marginBottom: '8px' }}>🌟</div>

        <p style={{
          color: '#e0e0e0',
          fontSize: '14px',
          lineHeight: 1.7,
          fontFamily: 'var(--font-sans)',
        }}>
          Число зрелости проявляется после 40–50 лет. Это синтез вашего Жизненного Пути и Судьбы —
          то, кем вы становитесь, пройдя через опыт и осознание.
        </p>

        {/* Большое число зрелости */}
        <div style={{ ...numberStyle, color: MATURITY_COLOR }}>
          {data.number}
        </div>

        {/* Описание */}
        {data.description && (
          <p style={textStyle}>{data.description}</p>
        )}

        {/* Компоненты: Life Path + Destiny */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
          gap: '12px',
          marginTop: '20px',
        }}>
          <div style={smallNumberCardStyle}>
            <div style={{ fontSize: '12px', color: '#888', marginBottom: '8px' }}>Жизненный путь</div>
            <div style={{ fontSize: '32px', fontWeight: 700, color: '#7DD3C0' }}>
              {data.lifePathNumber}
            </div>
          </div>
          <div style={smallNumberCardStyle}>
            <div style={{ fontSize: '12px', color: '#888', marginBottom: '8px' }}>Судьба</div>
            <div style={{ fontSize: '32px', fontWeight: 700, color: '#9382DC' }}>
              {data.destinyNumber}
            </div>
          </div>
        </div>

        {/* Статическая интерпретация */}
        {data.interpretation && (
          <div style={{ marginTop: '20px' }}>
            <div style={sectionTitleStyle}>📖 Значение числа зрелости:</div>
            <p style={textStyle}>{data.interpretation}</p>
          </div>
        )}

        {/* Рекомендации */}
        {data.recommendations && data.recommendations.length > 0 && (
          <div style={{ marginTop: '16px' }}>
            <div style={sectionTitleStyle}>🌟 Рекомендации:</div>
            {data.recommendations.map((rec, i) => (
              <div key={i} style={listItemStyle}>
                <span style={bulletStyle}>→</span>
                {rec}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* AI-интерпретация */}
      {data.aiInterpretation && (
        <div style={cardStyle}>
          <h4 style={{
            color: MATURITY_COLOR,
            fontSize: '16px',
            fontFamily: 'var(--font-display)',
            fontStyle: 'italic',
            marginBottom: '12px',
          }}>
            ⭐ Интерпретация
          </h4>
          <div style={aiTextStyle}>
            {data.aiInterpretation}
          </div>
        </div>
      )}
    </div>
  );
};

export default MaturityNumber;
